<div class="flex flex-col w-[60rem] max-w-full">
    <!-- Header / filters -->
    <h2 mat-dialog-title class="!text-xl !mb-2">
        Import Process Steps from Production
    </h2>
    <p class="text-sm text-secondary -mt-1 mb-4 px-6">
        Pick a category, process, and column from the Production &gt;
        Manufacture Processes table. The selected steps will be copied as new
        editable rows. Later edits in production won't sync back.
    </p>

    <!-- Filter row sits in the dialog header so users can refilter without losing the preview position -->
    <form [formGroup]="form" class="grid grid-cols-3 gap-3 px-6">
        <mat-form-field class="fuse-mat-dense" subscriptSizing="dynamic">
            <mat-label>Category</mat-label>
            <mat-select
                formControlName="categoryId"
                (selectionChange)="onCategoryChanged()"
            >
                @for (cat of categories; track cat.manufactureProcessCategoryId) {
                <mat-option [value]="cat.manufactureProcessCategoryId">{{
                    cat.name
                }}</mat-option>
                }
            </mat-select>
        </mat-form-field>

        <mat-form-field class="fuse-mat-dense" subscriptSizing="dynamic">
            <mat-label>Process</mat-label>
            <mat-select
                formControlName="processId"
                (selectionChange)="onProcessChanged()"
            >
                @for (proc of getProcesses(); track proc.manufactureProcessId) {
                <mat-option [value]="proc.manufactureProcessId">{{
                    proc.name
                }}</mat-option>
                }
            </mat-select>
        </mat-form-field>

        <mat-form-field class="fuse-mat-dense" subscriptSizing="dynamic">
            <mat-label>Column (e.g. variant)</mat-label>
            <mat-select
                formControlName="columnDefinitionId"
                (selectionChange)="onColumnChanged()"
            >
                @for (col of getColumns(); track col.manufactureProcessColumnDefinitionId) {
                <mat-option
                    [value]="col.manufactureProcessColumnDefinitionId"
                    >{{ col.name }}</mat-option
                >
                }
            </mat-select>
        </mat-form-field>
    </form>

    <!-- Body / preview -->
    <mat-dialog-content class="!pt-2">
        @if (!loadedProcess && !loading) {
        <div
            class="border border-dashed rounded p-8 text-center text-secondary"
        >
            Pick a category, process and column above to preview the steps that
            will be imported.
        </div>
        } @if (loading) {
        <div
            class="border border-dashed rounded p-8 text-center text-secondary"
        >
            Loading process steps...
        </div>
        } @if (loadedProcess && !loading) { @if (getStepCount() === 0) {
        <div
            class="border border-dashed rounded p-6 text-center text-secondary text-sm"
        >
            This process/column combination has no steps to import.
        </div>
        } @if (getStepCount() > 0) {
        <div class="flex flex-col rounded-lg border overflow-hidden">
            <!--
          Cost Centre column shows the rate that will be inherited from the
          parent stage (per the cost-centre-per-stage feature). Empty stages
          show a hyphen placeholder.
          -->
            <!-- Header -->
            <div
                class="grid grid-cols-12 bg-gray-50 py-2 px-4 gap-3 items-center uppercase text-sm tracking-tight border-b"
            >
                <span>#</span>
                <span class="col-span-2">Stage</span>
                <span class="col-span-3">Step</span>
                <span class="col-span-3">Cost Centre</span>
                <span class="col-span-2 text-right">Minutes</span>
                <span class="text-right">Hours</span>
            </div>
            <!-- Rows -->
            @for (row of getPreviewRows(); track row; let i = $index) {
            <div
                class="grid grid-cols-12 py-2 px-4 gap-3 items-center text-md border-b last:border-b-0"
                [class.bg-amber-50]="row.isHighlighted"
            >
                <span>{{ i + 1 }}</span>
                <span class="col-span-2">{{ row.stage || "-" }}</span>
                <span class="col-span-3">
                    {{ row.step }}
                    @if (row.isHighlighted) {
                    <span class="ml-1 text-xs text-amber-600">(waiting)</span>
                    }
                </span>
                <span class="col-span-3 text-sm">
                    @if (row.costCentreCode) {
                    {{ row.costCentreCode }} ({{
                        row.costCentreRate | currency: "GBP"
                    }}/hr) } @if (!row.costCentreCode) {
                    <span class="text-secondary">-</span>
                    }
                </span>
                <span class="col-span-2 text-right">{{
                    row.minutes | number: "1.0-2"
                }}</span>
                <span class="text-right">{{
                    row.minutes / 60 | number: "1.0-2"
                }}</span>
            </div>
            }
            <!-- Totals -->
            <div
                class="grid grid-cols-12 py-2 px-4 gap-3 items-center bg-gray-50 border-t font-medium"
            >
                <span class="col-span-9 text-right">Total</span>
                <span class="col-span-2 text-right">{{
                    getTotalMinutes() | number: "1.0-2"
                }}</span>
                <span class="text-right">{{
                    getTotalMinutes() / 60 | number: "1.0-2"
                }}</span>
            </div>
        </div>
        } }
    </mat-dialog-content>

    <!-- Actions -->
    <mat-dialog-actions class="!px-6 !pb-4 flex justify-end gap-2">
        <button mat-stroked-button (click)="cancel()" type="button">
            Cancel
        </button>
        <button
            mat-flat-button
            color="primary"
            type="button"
            [disabled]="!canImport()"
            (click)="confirm()"
        >
            <mat-icon
                class="icon-size-4 mr-1"
                svgIcon="heroicons_outline:arrow-down-on-square"
            ></mat-icon>
            Import {{ getStepCount() }} step(s) as a copy
        </button>
    </mat-dialog-actions>
</div>
